import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from '@phosphor-icons/react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading, StatsRow, CTABanner, ProcessFlowSVG } from '../components/SiteBlocks';
import { Button } from '../components/ui/button';

const STEPS = [
  {
    n: '01', t: 'Tell our AI what you need',
    d: 'Use the AI proposal generator on our homepage. Describe your project in plain English — a WhatsApp bot, an enterprise dashboard, a full mobile app. Our AI asks clarifying questions and structures your scope.',
    details: ['Conversational intake (60 seconds)', 'AI extracts scope, features, integrations', 'No signup required to get a proposal'],
  },
  {
    n: '02', t: 'AI generates your detailed proposal',
    d: 'Within 60 seconds, our AI generates a full proposal: executive summary, scope, recommended stack, timeline, team composition, milestones, and a budget range — backed by 500+ similar projects shipped.',
    details: ['Scope + feature breakdown', 'Recommended tech stack', 'Timeline + sprint plan', 'Budget range with confidence', 'Downloadable PDF'],
  },
  {
    n: '03', t: 'Discovery call & refinement',
    d: 'Our solution architect joins a 30-min call to validate the proposal, clarify edge cases, and confirm timeline + commercials. We iterate the proposal until it perfectly matches your needs.',
    details: ['30-min call with solution architect', 'Risk assessment + edge cases', 'Engagement model selection', 'Final fixed-price quote'],
  },
  {
    n: '04', t: 'Kickoff & sprint planning',
    d: 'We assemble your team — senior engineers + 8 specialist AI agents. Sprint plan is shared on Day 1. You meet your team. Daily standups, Friday demos, transparent Linear/Jira boards.',
    details: ['Team assembled in 1-2 weeks', 'Day 1 sprint plan', 'Daily standups + weekly reports', 'Direct Slack/Teams channel'],
  },
  {
    n: '05', t: 'AI-accelerated build',
    d: 'Each senior engineer is paired with 8 AI agents: Dev, Test, Review, UI, DevOps, Docs, Support, Data. The result is 60% faster delivery without quality compromise. Demos every Friday.',
    details: ['2-week sprints', 'Friday demo cadence', 'Pre-built CI/CD + observability', '90%+ test coverage'],
  },
  {
    n: '06', t: 'Launch & ongoing partnership',
    d: 'We launch your product, set up analytics, write the runbook, and stay on for warranty + iteration. You own 100% of the code. We\'re a partner, not a vendor.',
    details: ['App Store / production deploy', 'Analytics + monitoring setup', '2-week warranty', 'Optional managed services'],
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO
        path="/how-it-works"
        title="How It Works — AI-Accelerated Software Delivery"
        description="From a 60-second AI proposal to a production-grade product in 4-6 weeks. Discover XceedAgents' hybrid AI + senior engineering delivery process."
      />
      <Navbar />

      <SectionHero
        eyebrow="How It Works"
        title="From idea to production in 6 transparent steps."
        subtitle="Our hybrid AI + senior engineering model delivers production-grade software 60% faster than traditional offshore or onshore vendors. Here's exactly how."
      >
        <Link to="/start-project">
          <Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="how-cta">
            Request for Proposal <ArrowRight size={16} weight="bold" />
          </Button>
        </Link>
      </SectionHero>

      {/* Visual process flow */}
      <section className="py-16 lg:py-20" style={{ background: 'var(--surface)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <ProcessFlowSVG steps={['Intake', 'Proposal', 'Discovery', 'Build', 'Launch']} />
        </div>
      </section>

      {/* Detailed steps */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 space-y-16">
          {STEPS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="grid md:grid-cols-12 gap-8 items-start"
              data-testid={`step-${i}`}
            >
              <div className="md:col-span-3">
                <div className="font-heading text-6xl font-bold" style={{ color: 'var(--primary-accent)' }}>{s.n}</div>
              </div>
              <div className="md:col-span-9">
                <h3 className="font-heading text-2xl lg:text-3xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{s.t}</h3>
                <p className="text-lg leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>{s.d}</p>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {s.details.map((d, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <CheckCircle weight="fill" size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <StatsRow stats={[
        { value: '60 sec', label: 'AI proposal generation' },
        { value: '4-6 wk', label: 'MVP delivery' },
        { value: '60%', label: 'Faster than traditional' },
        { value: '100%', label: 'Code ownership' },
      ]} />

      <CTABanner
        title="Start your project the AI way."
        subtitle="Describe what you need. Our AI generates a detailed proposal in 60 seconds. No signup, no obligation."
        ctaText="Generate My Proposal"
        ctaHref="/start-project"
      />

      <Footer />
    </div>
  );
}
